import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  INVOICE_PAY_MOCK_SCENARIOS,
  type InvoicePayMockScenario,
} from '@entities/invoice-pay';
import { cn } from '@shared/lib/utils';
import { Button } from '@shared/ui';

interface InvoicePayMockPanelProps {
  scenario: InvoicePayMockScenario;
  onScenario: (scenario: InvoicePayMockScenario) => void;
  onToastDemo?: () => void;
}

export function InvoicePayMockPanel({
  scenario,
  onScenario,
  onToastDemo,
}: InvoicePayMockPanelProps) {
  const { t } = useTranslation('invoice-pay');
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        'fixed bottom-3 right-3 z-[90] w-[min(100%-1.5rem,320px)] rounded-lg border border-border bg-popover text-popover-foreground shadow-lg',
        'font-mono text-[11px] leading-tight'
      )}
      aria-label={t('mock.panelTitle')}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <p className="truncate font-sans text-xs font-semibold">{t('mock.panelTitle')}</p>
          <p className="truncate text-[10px] text-muted-foreground">{t('mock.panelHint')}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2 text-xs"
          onClick={() => setCollapsed((v) => !v)}
        >
          {collapsed ? t('mock.expand') : t('mock.collapse')}
        </Button>
      </div>

      {!collapsed ? (
        <div className="flex max-h-[50dvh] flex-col gap-1 overflow-y-auto p-2">
          {INVOICE_PAY_MOCK_SCENARIOS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onScenario(item.id)}
              className={cn(
                'rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent',
                scenario === item.id && 'bg-accent ring-1 ring-ring'
              )}
            >
              <span className="block font-sans text-xs font-medium">{item.label}</span>
              <span className="block text-[10px] text-muted-foreground">{item.hint}</span>
            </button>
          ))}

          {onToastDemo ? (
            <button
              type="button"
              onClick={onToastDemo}
              className="mt-1 rounded-md border border-dashed border-border px-2 py-1.5 text-left font-sans text-xs hover:bg-accent"
            >
              {t('mock.toastDemo')}
            </button>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}
